// app/features/industry-opportunities/components/OpportunityHeaderComponent.tsx
'use client';

import { ArrowLeft, Building2, TrendingUp } from 'lucide-react';
import Link from 'next/link';

import { cn } from '@/lib/utils';

import type { IndustryOpportunity } from '../industry-opportunity.interface';
import { CATEGORY_CONFIG, getCategoryKey } from '../industry-opportunity.utils';

interface OpportunityHeaderComponentProps {
  opportunity: IndustryOpportunity;
  industryName: string;
  industrySlug: string;
}

// Get header background color based on category
const CATEGORY_HEADER_COLORS: Record<string, string> = {
  'advisor-lifecycle': 'bg-purple-dark',
  'compliance': 'bg-orange',
  'client-communication': 'bg-blue',
  'client-updates': 'bg-terracotta',
  'reporting': 'bg-green',
  'operations': 'bg-teal',
  'data-search': 'bg-indigo',
  'onboarding': 'bg-brown',
  'growth': 'bg-yellow-dark',
  'custom': 'bg-stone-600',
};

export function OpportunityHeaderComponent({ 
  opportunity,
  industryName,
  industrySlug
}: OpportunityHeaderComponentProps) {
  // Parse primary metric to extract key numbers with context
  const extractMetricHighlight = (metric: string | null): { value: string; context: string } | null => {
    if (!metric) return null;
    
    // Try to match percentage patterns first (e.g., "90% reduction")
    const percentMatch = metric.match(/(\d+[-–]?\d*%)\s*(reduction|improvement|increase)?/i);
    if (percentMatch) {
      return { 
        value: percentMatch[1], 
        context: percentMatch[2] || 'improvement' 
      };
    }
    
    // Match time patterns (e.g., "24-48 hours", "5-8 hours")
    const timeMatch = metric.match(/(\d+[-–]?\d*)\s*(hours?|days?|weeks?|mins?|minutes?)/i);
    if (timeMatch) {
      return { 
        value: `${timeMatch[1]} ${timeMatch[2]}`, 
        context: 'target' 
      };
    }
    
    // Match basis points (e.g., "50-80bps")
    const bpsMatch = metric.match(/(\d+[-–]?\d*)\s*bps/i);
    if (bpsMatch) {
      return { 
        value: `${bpsMatch[1]} bps`, 
        context: 'tax alpha' 
      };
    }
    
    // Match multipliers (e.g., "3x more")
    const multiplierMatch = metric.match(/(\d+x)\s*(more)?/i);
    if (multiplierMatch) {
      return { 
        value: multiplierMatch[1], 
        context: 'capacity' 
      };
    }
    
    return null;
  };

  const metricHighlight = extractMetricHighlight(opportunity.primary_metric);
  
  // Get category for color scheme
  const categoryKey = getCategoryKey(opportunity);
  const categoryConfig = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG['custom'];
  const headerBgColor = CATEGORY_HEADER_COLORS[categoryKey] || 'bg-stone-600';
  
  // Get card_description from metadata if available
  const cardDescription = opportunity.metadata_json && 
    typeof opportunity.metadata_json === 'object' && 
    'card_description' in opportunity.metadata_json
    ? String(opportunity.metadata_json.card_description)
    : null;
  
  // Main title is the card_description (punchy tagline), subtitle is the problem title
  const mainTitle = cardDescription || opportunity.solution_headline || opportunity.problem_title;
  const subtitle = cardDescription ? opportunity.problem_title : null;

  return (
    <div className={cn("relative overflow-hidden text-white", headerBgColor)}>
      {/* Background Pattern & Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-black/20" />
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-repeat opacity-[0.03]" />
        {/* Decorative orbs */}
        <div className="absolute top-10 right-[20%] w-64 h-64 rounded-full blur-3xl bg-white/5" />
        <div className="absolute bottom-10 left-[10%] w-48 h-48 rounded-full blur-2xl bg-white/5" />
      </div>
      
      <div className="relative z-10 px-4 md:container md:mx-auto md:px-4 py-12 md:py-16 lg:py-20">
        {/* Back Arrow + Industry Tag */}
        <div className="flex items-center gap-3 mb-8">
          <Link 
            href={`/solutions/industries/${industrySlug}`}
            className="inline-flex items-center justify-center w-10 h-10 hover:opacity-80 transition-opacity bg-white/10 backdrop-blur-sm rounded-full"
          >
            <ArrowLeft size={18} />
          </Link>
          <div className="flex items-center gap-2 text-sm font-medium bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <Building2 size={14} />
            <span>{industryName}</span>
          </div>
        </div>
        
        {/* Main Hero Content */}
        <div className="max-w-5xl">
          {/* Main Title - Solution/Product Name */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight mb-4 tracking-tight">
            {mainTitle}
          </h1>
          
          {/* Subtitle - Problem Title */}
          {subtitle && (
            <p className="text-xl md:text-2xl leading-relaxed opacity-90 mb-6 max-w-3xl">
              {subtitle}
            </p>
          )}
        </div>

        {/* Stats Row - Only essential metrics */}
        {metricHighlight && (
          <div className="flex flex-wrap gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-5 min-w-[160px]">
              <div className="flex items-center gap-2 text-xs font-mono uppercase mb-2 opacity-80">
                <TrendingUp size={14} />
                Expected Impact
              </div>
              <div className="text-3xl font-black">
                {metricHighlight.value}
              </div>
              <p className="text-xs opacity-70 mt-1 line-clamp-1">{metricHighlight.context}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}





